import { useState } from 'react';
import { CpmButton } from '../../../../components/design-system';
import { CPM_EXCISE_SUBCATEGORY_MAX_LENGTH } from '../../../../constants/centralProductMaster.constants';
import type { CpmExciseSubCategory } from '../../../../types/centralProductMaster.types';

interface SubCategoryEditorProps {
    subs: CpmExciseSubCategory[];
    busy?: boolean;
    onAdd: (name: string) => Promise<void> | void;
    onRename: (subId: string, name: string) => Promise<void> | void;
    onRemove: (subId: string) => Promise<void> | void;
}

export default function SubCategoryEditor({
    subs,
    busy = false,
    onAdd,
    onRename,
    onRemove,
}: SubCategoryEditorProps) {
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editVal, setEditVal] = useState('');
    const [adding, setAdding] = useState(false);
    const [newVal, setNewVal] = useState('');

    const subHeading = subs.length ? `Subcategories · ${subs.length}` : 'Subcategories';

    return (
        <div className="flex flex-col min-w-0" style={{ gap: 9 }}>
            <span
                className="uppercase text-[var(--cpm-text-secondary)]"
                style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em' }}
            >
                {subHeading}
            </span>

            {subs.length > 0 ? (
                <div className="flex flex-col" style={{ gap: 6 }}>
                    {subs.map((s) => {
                        const editing = editingId === s._id;
                        return (
                            <div
                                key={s._id}
                                className="flex items-center rounded-[10px] bg-white border border-[var(--cpm-border-default)]"
                                style={{ gap: 10, padding: '9px 12px' }}
                            >
                                <span
                                    className="shrink-0 rounded-full bg-[var(--cpm-brand-purple)]"
                                    style={{ width: 6, height: 6 }}
                                />
                                {editing ? (
                                    <>
                                        <input
                                            value={editVal}
                                            onChange={(e) => setEditVal(e.target.value)}
                                            maxLength={CPM_EXCISE_SUBCATEGORY_MAX_LENGTH}
                                            className="flex-1 min-w-0 rounded-[8px] border-[1.5px] border-[var(--cpm-border-strong)] bg-white text-[var(--cpm-brand-navy)]"
                                            style={{
                                                height: 32,
                                                padding: '0 10px',
                                                fontSize: 13,
                                                fontWeight: 700,
                                            }}
                                            autoFocus
                                        />
                                        <button
                                            type="button"
                                            disabled={busy || !editVal.trim()}
                                            onClick={async () => {
                                                const v = editVal.trim();
                                                if (!v) return;
                                                await onRename(s._id, v);
                                                setEditingId(null);
                                            }}
                                            className="inline-flex items-center rounded-[8px] bg-[var(--cpm-brand-navy)] text-white disabled:opacity-50"
                                            style={{
                                                height: 32,
                                                padding: '0 12px',
                                                fontSize: 11.5,
                                                fontWeight: 800,
                                                cursor: 'pointer',
                                                border: 'none',
                                            }}
                                        >
                                            Save
                                        </button>
                                        <button
                                            type="button"
                                            disabled={busy}
                                            onClick={() => setEditingId(null)}
                                            className="inline-flex items-center rounded-[8px] border-[1.5px] border-[var(--cpm-border-strong)] bg-white text-[var(--cpm-text-secondary)]"
                                            style={{
                                                height: 32,
                                                padding: '0 10px',
                                                fontSize: 11.5,
                                                fontWeight: 800,
                                                cursor: 'pointer',
                                            }}
                                        >
                                            Cancel
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <span
                                            className="flex-1 min-w-0 text-[var(--cpm-text-primary)]"
                                            style={{ fontSize: 13, fontWeight: 700 }}
                                        >
                                            {s.name}
                                        </span>
                                        <button
                                            type="button"
                                            disabled={busy}
                                            onClick={() => {
                                                setEditingId(s._id);
                                                setEditVal(s.name);
                                            }}
                                            className="inline-flex items-center justify-center rounded-[8px] border-[1.5px] border-[var(--cpm-border-strong)] bg-white text-[var(--cpm-text-secondary)] hover:border-[var(--cpm-brand-purple)] hover:text-[var(--cpm-brand-purple)]"
                                            style={{ width: 28, height: 28, cursor: 'pointer' }}
                                            title="Rename subcategory"
                                        >
                                            <svg
                                                width="13"
                                                height="13"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                            >
                                                <path d="M12 20h9" />
                                                <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
                                            </svg>
                                        </button>
                                        <button
                                            type="button"
                                            disabled={busy}
                                            onClick={() => onRemove(s._id)}
                                            className="inline-flex items-center justify-center rounded-[8px] border-[1.5px] border-[var(--cpm-border-strong)] bg-white text-[var(--cpm-text-secondary)] hover:border-[var(--cpm-shortfall-text)] hover:text-[var(--cpm-shortfall-text)]"
                                            style={{ width: 28, height: 28, cursor: 'pointer' }}
                                            title="Remove subcategory"
                                        >
                                            <svg
                                                width="13"
                                                height="13"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.2"
                                            >
                                                <path d="M18 6 6 18" />
                                                <path d="m6 6 12 12" />
                                            </svg>
                                        </button>
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <span
                    style={{
                        fontSize: 12.5,
                        fontWeight: 600,
                        lineHeight: 1.45,
                        color: 'var(--cpm-text-secondary)',
                    }}
                >
                    No subcategories. Many categories outside a state&apos;s premium tiers don&apos;t
                    have any — that&apos;s expected, not missing data.
                </span>
            )}

            {adding ? (
                <div className="flex items-center" style={{ gap: 8 }}>
                    <input
                        value={newVal}
                        onChange={(e) => setNewVal(e.target.value)}
                        maxLength={CPM_EXCISE_SUBCATEGORY_MAX_LENGTH}
                        placeholder="Subcategory name, e.g. Premium"
                        className="flex-1 min-w-0 rounded-[10px] border-[1.5px] border-[var(--cpm-border-strong)] bg-white text-[var(--cpm-brand-navy)]"
                        style={{ height: 36, padding: '0 12px', fontSize: 13, fontWeight: 700 }}
                        autoFocus
                    />
                    <button
                        type="button"
                        disabled={busy || !newVal.trim()}
                        onClick={async () => {
                            const v = newVal.trim();
                            if (!v) return;
                            await onAdd(v);
                            setAdding(false);
                            setNewVal('');
                        }}
                        className="inline-flex items-center rounded-[10px] bg-[var(--cpm-brand-navy)] text-white disabled:opacity-50"
                        style={{
                            height: 36,
                            padding: '0 14px',
                            fontSize: 12.5,
                            fontWeight: 800,
                            cursor: 'pointer',
                            border: 'none',
                        }}
                    >
                        Add
                    </button>
                    <button
                        type="button"
                        disabled={busy}
                        onClick={() => {
                            setAdding(false);
                            setNewVal('');
                        }}
                        className="inline-flex items-center rounded-[10px] border-[1.5px] border-[var(--cpm-border-strong)] bg-white text-[var(--cpm-text-secondary)]"
                        style={{
                            height: 36,
                            padding: '0 12px',
                            fontSize: 12.5,
                            fontWeight: 800,
                            cursor: 'pointer',
                        }}
                    >
                        Cancel
                    </button>
                </div>
            ) : (
                <CpmButton
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => {
                        setAdding(true);
                        setNewVal('');
                    }}
                    className="!self-start !border-dashed"
                    leadingIcon={
                        <svg
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                        >
                            <path d="M12 5v14" />
                            <path d="M5 12h14" />
                        </svg>
                    }
                >
                    Add subcategory
                </CpmButton>
            )}
        </div>
    );
}
